import { FC, useContext, useEffect, useState } from 'react';
import { LoadingStatus } from '../../../@types/enumsGlobal';
import { DefaultGrid } from '../../../components/grids/default/defaultGrid';
import { PluginWrapper, ToolbarWrapper } from '../../../components/plugins';
import { IGoodsViewDTO } from '../../../libs/coreapi-dto/dirs/goods';
import { IGosContractGoodsViewDTO } from '../../../libs/coreapi-dto/dirs/gosContract';
import { GoodsDataProvider } from '../../../Services/DataProviders/GoodsDataProvider';
import useGridFilter, { CreateSelectorGridFilter, GridFilterAction } from '../../../system/hooks/useGridFilter';
import { useAppContext } from '../../../system/providers/appContextProvider';
import { usePluginContext } from '../../../system/providers/plugin';
import GoodsCreatorModal from './GoodsCreatorModal';
import styles from '../../styles/index.module.scss';
import { TreeViewPanel } from '../../../components/treeViewPanel';
import { GoodsGroupDataProvider } from '../../../Services/DataProviders/GoodsGroupDataProvider';
import { GosContractDataProvider } from '../../../Services/DataProviders/GosContractDataProvider';
import classNames from 'classnames';
import DefaultTreeViewCommandsPanel from '../../../components/commandsPanels/DefaultTreeViewCommandsPanel';
import { GoodsGroupCreatorModal, GoodsGroupSelectorModal } from '../GoodsGroup';
import { useTabsContext } from '../../../system/providers/tabsProvider';
import { Spinner } from '../../../components/spiner/Spinner';
import { useGridFilterValidation } from '../../../system/hooks/useGridFilterValidation';
import { GosContractItemsPluginSettings } from '../GosContract/pluginSettings/gosContractItemsPluginSettings';

const GoodsGrid: FC<IGridProps> = (props) => {
    const appCtx = useAppContext();
    const pluginCtx = usePluginContext();
    const tabCtx = useTabsContext();
    const goodsDataProvider = new GoodsDataProvider(appCtx.coreApiService);
    const goodsGroupDataProvider = new GoodsGroupDataProvider(appCtx.coreApiService);
    const gosContractDataProvider = new GosContractDataProvider(appCtx.coreApiService);

    const mapGosContractRows = (rows: IGosContractGoodsViewDTO[]): IGoodsViewDTO[] =>
        rows.map((item) => ({
            ...(item as unknown as Partial<IGoodsViewDTO>),
            idGlobal: item.idGlobal,
            displayName: item.specName ?? item.goodsCode ?? '',
            name: item.specName ?? item.goodsCode ?? '',
            nameShort: item.specName ?? item.goodsCode ?? '',
            description: item.comment ?? '',
            code: item.goodsCode ?? '',
            dosage: item.dosage ?? '',
            producerName: item.specProducer ?? '',
            cureFormName: item.displayUnit ?? '',
            substanceName: item.mnnName ?? '',
            important: item.isLgota,
            registerPrice: item.price,
            maxPriceSal: item.price,
            nFv: item.qty ?? 0,
            lgota: item.isLgota ? 1 : 0,
        })) as IGoodsViewDTO[];

    const [data, setData] = useState<IGoodsViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>('view');
    const [goodsGroupViewState, setGoodsGroupViewState] = useState<TreeViewStateType>('view');
    const [gridFilter, dispatchGridFilter] = useGridFilter(props.baseGridFilter);
    const [goodsGroupGridFilter] = useGridFilter();
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [goodsGroupFolders, setGoodsGroupFolders] = useState<ITreeViewFolder[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const { hasFilter } = useGridFilterValidation(gridFilter);    
    const pluginSettings = props.gosContract ? GosContractItemsPluginSettings : props.plugin

    const handleItemsChange = (newItems: IGridRow[]) => {
        setSelectedItems(newItems);
        props?.onMultipleSelect?.(newItems);
    };
    
    useEffect(() => {
        if (pluginCtx.treeView.visiblePanel) {
            if (goodsGroupViewState === 'view' || goodsGroupViewState === 'return') {
                goodsGroupDataProvider.getView(goodsGroupGridFilter, (entities) => {
                    let tmpList: ITreeViewFolder[] = [];
                    entities.forEach((item) => {
                        tmpList.push({ folderId: item.idGlobal, displayName: item.name, childGroups: item.childGroups });
                    });
                    setGoodsGroupFolders(tmpList);
                    setGoodsGroupViewState('view')
                });
            }
        } else {
            setGoodsGroupFolders([]);
        }
    }, [pluginCtx.treeView.visiblePanel, goodsGroupViewState]);

    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        setIsSubmitting(true);

        const handleData = (data: any[], totalCount: number) => {
            if (!signal.aborted) {
                const items = props.gosContract
                    ? mapGosContractRows(data as IGosContractGoodsViewDTO[])
                    : (data as IGoodsViewDTO[]);
                setData(items);
                setTotalCount(totalCount);
                setLoadingStatus(items.length > 0 ? LoadingStatus.Completed : LoadingStatus.NoData);
                setIsSubmitting(false);
            }
        };

        const handleDataWithPagination = (data: any[], totalCount: number) => {
            if (signal.aborted) return;
            
            if (props.gosContract && totalCount > data.length) {
                const updatedFilter = {
                    ...gridFilter,
                    numberPerPage: totalCount,
                    columnFilters: [],
                    paramFilter: {
                        idGosContractGlobal: props.gosContract,
                    },
                };

                gosContractDataProvider.getItemsView(props.gosContract, updatedFilter, handleData);
                return;
            }

            if (props.multipleSelect && hasFilter && totalCount > gridFilter.numberPerPage) {
                
                const updatedFilter = { ...gridFilter, numberPerPage: totalCount };
                const finalFilter = props.gosContract ? {
                    ...updatedFilter,
                    paramFilter: {
                        ...updatedFilter.paramFilter,
                        idGosContractGlobal: props.gosContract
                    }
                } : updatedFilter as IGridFilter;

                if (props.getUniqueGoodsByStore) {
                    goodsDataProvider.getDistinctViewByStore(finalFilter, handleData, signal);
                } else {
                    goodsDataProvider.getViewByName(finalFilter, handleData, signal);
                }
            } else {                
                handleData(data, totalCount);
            }
        };

        const baseFilter = props.gosContract ? {
            ...gridFilter,
            numberPerPage: 1000,
            fieldOrderBy: undefined,
            columnFilters: props.baseGridFilter?.paramFilter.goodsName? [{
                name: "specName",
                value: `${props.baseGridFilter?.paramFilter.goodsName}%`,
                operator: "Like" as const
            }] : gridFilter.columnFilters.filter(filter => filter.name !== "deleted") || [],
            paramFilter: {
                idGosContractGlobal: props.gosContract
            }
        } : gridFilter;

        if (props.gosContract) {
            gosContractDataProvider.getItemsView(props.gosContract, baseFilter, handleDataWithPagination);
        } else if (props.multipleSelect && hasFilter) {
            if (props.getUniqueGoodsByStore) {
                goodsDataProvider.getDistinctViewByStore(baseFilter, handleDataWithPagination, signal);
            } else {
                goodsDataProvider.getViewByName(baseFilter, handleDataWithPagination, signal);
            }
        } else {
            if (props.getUniqueGoodsByStore) {
                goodsDataProvider.getDistinctViewByStore(baseFilter, handleData, signal);
            } else {
                goodsDataProvider.getViewByName(baseFilter, handleData, signal);
            }
        }

        return () => {
            abortController.abort('abort');
        };
    }, [viewState, gridFilter, props.gosContract]);

    useEffect(() => {
        pluginCtx.masterGrid.onUnselectEvent();
    }, [tabCtx.currentTab]);

    useEffect(() => {
        if (props.dataFromPluginGridSelectorInput) {
            setSelectedItems(props.dataFromPluginGridSelectorInput);
        }
    }, [props.dataFromPluginGridSelectorInput]);

    return (
        <PluginWrapper>
            <ToolbarWrapper>
                <DefaultTreeViewCommandsPanel
                    selectedItem={[selectedItem, setSelectedItem]}
                    gridFilter={[gridFilter, dispatchGridFilter]}
                    dataProvider={goodsDataProvider}
                    groupDataProvider={goodsGroupDataProvider}
                    creatorModalJsx={GoodsCreatorModal}
                    creatorGroupModalJsx={GoodsGroupCreatorModal}
                    selectorGroupModalJsx={GoodsGroupSelectorModal}
                    pluginSettings={pluginSettings}
                    isModal={props?.isModal}
                    dispatchGridFilter={(value: GridFilterAction) => dispatchGridFilter(value)}
                    setGroupViewState={(vs) => setGoodsGroupViewState(vs)}
                    onFocusChanged={props?.onFocusChanged}
                    showCheckBoxDeleted={props.showCheckBoxDeleted}
                    idGosContract={props.gosContract}
                    popOverReport={{
                        items: props.dataFromPluginGridSelectorInput ? selectedItems : [],
                        onItemsChange: handleItemsChange
                    }}
                />
            </ToolbarWrapper>

            <div className={styles.pluginWorkspace}>
                {pluginCtx.treeView.visiblePanel && (
                    <>
                        <div className={styles.treeView}>
                            <TreeViewPanel
                                folders={goodsGroupFolders}
                                selectedFolder={pluginCtx.treeView.selectedFolder}
                                onHideClick={() => pluginCtx.treeView.onVisiblePanelEvent(false)}
                                onSelectFolder={(folder) => pluginCtx.treeView.onSelectEvent(folder)}
                                onExpandFolder={(folder, callback) => {
                                    goodsGroupDataProvider.getViewById(folder.folderId, goodsGroupGridFilter, (entities) => {
                                        let tmpList: ITreeViewFolder[] = [];
                                        entities.forEach((item) => {
                                            tmpList.push({
                                                folderId: item.idGlobal,
                                                displayName: item.name,
                                                childGroups: item.childGroups,
                                            });
                                        });
                                        callback(tmpList);
                                    });
                                }}
                                setGroupViewState={(value) => setGoodsGroupViewState(value)}
                                groupDataProvider={goodsGroupDataProvider}
                                pluginSettings={props.plugin}
                            />
                        </div>
                        <div className={classNames(styles.separator, styles.separator_vertical)}></div>
                    </>
                )}

                <div className={styles.gridWrapper}>
                    <div className={styles.masterGrid}>
                        {isSubmitting ? (
                            <Spinner />
                        ) : (
                            <DefaultGrid
                                showQuantity
                                gridId={props.gridId}
                                data={data}
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                totalCount={totalCount}
                                filter={gridFilter}
                                separator
                                isSearchForMultyselect={props.isSearchForMultyselect}
                                singleDirectory
                                dataProvider={goodsDataProvider}
                                plugin={props.plugin}
                                selectedItem={selectedItem}
                                selectedItems={selectedItems}
                                getView={(innerFilter, callback) => {
                                    if (props.gosContract) {
                                        const contractFilter = {
                                            ...innerFilter,
                                            columnFilters: props.baseGridFilter?.paramFilter.goodsName? [{
                                                name: "specName",
                                                value: `${props.baseGridFilter?.paramFilter.goodsName}%`,
                                                operator: "Eq" as const
                                            }] : [],
                                            fieldOrderBy: undefined,
                                            paramFilter: {
                                                ...(innerFilter.paramFilter || {}),
                                                idGosContractGlobal: props.gosContract,
                                            },
                                        };
                                        gosContractDataProvider.getItemsView(
                                            props.gosContract,
                                            contractFilter,
                                            (entities) => callback(mapGosContractRows(entities))
                                        );
                                        return;
                                    }

                                    if (props.getUniqueGoodsByStore) {
                                        goodsDataProvider.getDistinctViewByStore(innerFilter, (entities) => {
                                            callback(entities);
                                        });
                                    } else {
                                        goodsDataProvider.getViewByName(innerFilter, (entities) => {
                                            callback(entities);
                                        });
                                    }
                                }}
                                onSelect={(row) => {
                                    setSelectedItem(row);
                                    props?.onSelect?.(
                                        row,
                                        data.find((x) => x.idGlobal === row?.idGlobal)
                                    );
                                }}
                                onMultipleSelect={(rows) => {
                                    setSelectedItems(rows);
                                    props?.onMultipleSelect?.(rows);
                                }}
                                multipleSelect={props.multipleSelect}
                                onDoubleClick={(i) => {
                                    props.onDoubleClick?.(
                                        i,
                                        data.find((x) => x.idGlobal === i.idGlobal)
                                    )
                                }}
                                onSort={(i) => dispatchGridFilter({ type: 'sort', payload: i.propertyName })}
                                onFilterDelete={(i) =>
                                    dispatchGridFilter({ type: 'deleteColumnFilter', payload: i.propertyName })
                                }
                                onPageNumberChange={(n) =>
                                    dispatchGridFilter({ type: 'changePageNumber', payload: { pageNumber: n } })
                                }
                                onNumberPerPageChange={(n) =>
                                    dispatchGridFilter({ type: 'changeNumberPerPage', payload: { numberPerPage: n } })
                                }
                                onEnter={() => setViewState('edit')}
                            />
                        )}
                    </div>
                </div>
            </div>
        </PluginWrapper>
    );
};

export default GoodsGrid;
